import React, { useCallback, useEffect, useState } from "react";
import {
  closePeriod,
  createRule,
  deleteRule,
  getJournalSyncRuns,
  getJournalSyncStatus,
  getPeriods,
  getRuleFields,
  getRules,
  getUnmappedValues,
  importOneCReport,
  openPeriod,
  previewRule,
  reorderRules,
  triggerJournalSync,
  updateRule,
} from "../../../api/financeApi";
import "../Reports/Reports.css";
import "./Administration.css";

const TABS = [
  { id: "rules", label: "Правила" },
  { id: "import", label: "Загрузка" },
  { id: "periods", label: "Периоды" },
  { id: "unmapped", label: "Нераспознанное" },
];

const emptyCondition = () => ({ field: "", operator: "contains", value: "" });

const emptyRule = (ruleType) => ({
  id: null,
  rule_type: ruleType,
  name: "",
  description: "",
  is_active: true,
  items: [emptyCondition()],
  logic: "and",
  setField: "",
  setValue: "",
  accountDt: "",
  accountCt: "",
  amountPercent: "",
  comment: "",
});

function Administration() {
  const [tab, setTab] = useState("rules");
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  return (
    <div className="finances-administration">
      <div className="fin-tabs">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`fin-chip${tab === item.id ? " is-active" : ""}`}
            onClick={() => {
              setTab(item.id);
              setError(null);
              setInfo(null);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
      {error && <div className="fin-error">{error}</div>}
      {info && <div className="fin-muted">{info}</div>}
      {tab === "rules" && <RulesTab onError={setError} onInfo={setInfo} />}
      {tab === "import" && <ImportTab onError={setError} onInfo={setInfo} />}
      {tab === "periods" && <PeriodsTab onError={setError} onInfo={setInfo} />}
      {tab === "unmapped" && <UnmappedTab onError={setError} />}
    </div>
  );
}

function RulesTab({ onError, onInfo }) {
  const [ruleType, setRuleType] = useState(1);
  const [rules, setRules] = useState([]);
  const [fields, setFields] = useState(null);
  const [draft, setDraft] = useState(() => emptyRule(1));
  const [preview, setPreview] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    const [list, ruleFields] = await Promise.all([
      getRules({ ruleType }),
      getRuleFields(),
    ]);
    setRules(Array.isArray(list) ? list : []);
    setFields(ruleFields);
  }, [ruleType]);

  useEffect(() => {
    load().catch((error) => onError(error.message));
  }, [load, onError]);

  const payload = () => {
    const body = {
      rule_type: Number(ruleType),
      name: draft.name,
      description: draft.description || null,
      is_active: draft.is_active,
      condition: {
        logic: draft.logic,
        items: draft.items
          .filter((item) => item.field && item.operator)
          .map((item) => ({
            field: item.field,
            operator: item.operator,
            ...(item.operator === "is_empty" || item.operator === "is_not_empty"
              ? {}
              : { value: item.value }),
          })),
      },
      set: {},
      create: null,
    };
    if (Number(ruleType) === 2) {
      body.create = {
        account_dt: draft.accountDt,
        account_ct: draft.accountCt,
        amount_percent: draft.amountPercent === "" ? null : Number(draft.amountPercent),
        set: {},
        comment: draft.comment || null,
      };
    } else if (draft.setField) {
      body.set = { [draft.setField]: draft.setValue };
    }
    return body;
  };

  const save = async () => {
    onError(null);
    try {
      const body = payload();
      if (draft.id) {
        const { rule_type: unusedType, ...update } = body;
        void unusedType;
        await updateRule(draft.id, update);
      } else {
        await createRule(body);
      }
      setDraft(emptyRule(ruleType));
      onInfo("Правило сохранено");
      await load();
    } catch (error) {
      onError(error.message);
    }
  };

  const runPreview = async () => {
    onError(null);
    try {
      const result = await previewRule(payload(), { dateFrom, dateTo });
      setPreview(result);
    } catch (error) {
      onError(error.message);
    }
  };

  const move = async (index, direction) => {
    const next = [...rules];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    try {
      const ordered = await reorderRules({
        rule_type: Number(ruleType),
        ids: next.map((rule) => rule.id),
      });
      setRules(Array.isArray(ordered) ? ordered : next);
    } catch (error) {
      onError(error.message);
    }
  };

  const conditionFields = (fields?.condition_fields?.[String(ruleType)] || []).map((value) => ({
    value,
    label: value,
  }));
  const operatorsFor = (field) => {
    const name = String(field || "").split(".").pop();
    const kind = (fields?.date_fields || []).includes(name)
      ? "date"
      : (fields?.number_fields || []).includes(name)
        ? "number"
        : "text";
    return (fields?.operators?.[kind] || []).map((value) => ({ value, label: value }));
  };
  const actionFields = (fields?.action_fields || []).map((value) => ({ value, label: value }));

  return (
    <div className="fin-panel">
      <div className="fin-panel__row">
        {[1, 2, 3].map((type) => (
          <button
            key={type}
            type="button"
            className={`fin-chip${Number(ruleType) === type ? " is-active" : ""}`}
            onClick={() => {
              setRuleType(type);
              setDraft(emptyRule(type));
              setPreview(null);
            }}
          >
            Тип {type}
          </button>
        ))}
      </div>

      {rules.map((rule, index) => (
        <div key={rule.id} className="fin-rule">
          <div>
            <div className="fin-rule__name">
              {rule.name} {!rule.is_active && <span className="fin-muted">выключено</span>}
            </div>
            <div className="fin-muted">{rule.description}</div>
          </div>
          <div className="fin-actions">
            <button type="button" className="fin-btn" onClick={() => move(index, -1)}>Вверх</button>
            <button type="button" className="fin-btn" onClick={() => move(index, 1)}>Вниз</button>
            <button
              type="button"
              className="fin-btn"
              onClick={() =>
                setDraft({
                  ...emptyRule(rule.rule_type),
                  id: rule.id,
                  name: rule.name,
                  description: rule.description || "",
                  is_active: rule.is_active,
                  logic: rule.condition?.logic || "and",
                  items: rule.condition?.items?.length ? rule.condition.items : [emptyCondition()],
                  accountDt: rule.create?.account_dt || "",
                  accountCt: rule.create?.account_ct || "",
                  amountPercent: rule.create?.amount_percent || "",
                  comment: rule.create?.comment || "",
                  setField: Object.keys(rule.set || {})[0] || "",
                  setValue: Object.values(rule.set || {})[0] || "",
                })
              }
            >
              Изменить
            </button>
            <button
              type="button"
              className="fin-btn"
              onClick={async () => {
                try {
                  await deleteRule(rule.id);
                  await load();
                } catch (error) {
                  onError(error.message);
                }
              }}
            >
              Удалить
            </button>
          </div>
        </div>
      ))}

      <div className="fin-stack">
        <label>
          Название
          <input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
        </label>
        <label>
          Описание
          <input
            value={draft.description}
            onChange={(event) => setDraft({ ...draft, description: event.target.value })}
          />
        </label>
        <label>
          <span>
            <input
              type="checkbox"
              checked={draft.is_active}
              onChange={(event) => setDraft({ ...draft, is_active: event.target.checked })}
            />{" "}
            Активно
          </span>
        </label>
        {draft.items.map((item, index) => (
          <div key={`${item.field}-${index}`} className="fin-grid">
            <label>
              Поле
              <select
                value={item.field}
                onChange={(event) => {
                  const items = [...draft.items];
                  items[index] = { ...item, field: event.target.value };
                  setDraft({ ...draft, items });
                }}
              >
                <option value="">Выберите поле</option>
                {conditionFields.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              Оператор
              <select
                value={item.operator}
                onChange={(event) => {
                  const items = [...draft.items];
                  items[index] = { ...item, operator: event.target.value };
                  setDraft({ ...draft, items });
                }}
              >
                {(operatorsFor(item.field).length
                  ? operatorsFor(item.field)
                  : [{ value: item.operator, label: item.operator }]
                ).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              Значение
              <input
                value={item.value || ""}
                onChange={(event) => {
                  const items = [...draft.items];
                  items[index] = { ...item, value: event.target.value };
                  setDraft({ ...draft, items });
                }}
              />
            </label>
          </div>
        ))}
        <button
          type="button"
          className="fin-btn"
          onClick={() => setDraft({ ...draft, items: [...draft.items, emptyCondition()] })}
        >
          Добавить условие
        </button>

        {Number(ruleType) === 2 ? (
          <div className="fin-grid">
            <label>
              Счёт Дт
              <input value={draft.accountDt} onChange={(event) => setDraft({ ...draft, accountDt: event.target.value })} />
            </label>
            <label>
              Счёт Кт
              <input value={draft.accountCt} onChange={(event) => setDraft({ ...draft, accountCt: event.target.value })} />
            </label>
            <label>
              Процент суммы
              <input
                value={draft.amountPercent}
                onChange={(event) => setDraft({ ...draft, amountPercent: event.target.value })}
              />
            </label>
          </div>
        ) : (
          <div className="fin-grid">
            <label>
              Поле действия
              <select
                value={draft.setField}
                onChange={(event) => setDraft({ ...draft, setField: event.target.value })}
              >
                <option value="">Не менять поле</option>
                {actionFields.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              Значение
              <input
                value={draft.setValue}
                placeholder="Сотовая связь"
                onChange={(event) => setDraft({ ...draft, setValue: event.target.value })}
              />
            </label>
          </div>
        )}

        <div className="fin-grid">
          <label>
            Превью с
            <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label>
            Превью по
            <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </label>
        </div>
        <div className="fin-actions">
          <button type="button" className="fin-btn" onClick={runPreview}>Проверить</button>
          <button type="button" className="fin-btn fin-btn--primary" onClick={save}>
            {draft.id ? "Обновить" : "Создать"}
          </button>
        </div>
        {preview && (
          <div className="fin-muted">
            Совпало {preview.matched} из {preview.checked}, сумма {preview.amount_kzt}
          </div>
        )}
      </div>
    </div>
  );
}

function ImportTab({ onError, onInfo }) {
  const [base, setBase] = useState("AQMOL");
  const [file, setFile] = useState(null);
  const [runSync, setRunSync] = useState(true);
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);

  const refresh = async () => {
    const [current, history] = await Promise.all([
      getJournalSyncStatus(),
      getJournalSyncRuns({ limit: 10 }),
    ]);
    setStatus(current);
    setRuns(Array.isArray(history) ? history : []);
  };

  useEffect(() => {
    refresh().catch((error) => onError(error.message));
  }, [onError]);

  return (
    <div className="fin-panel">
      <div className="fin-stack">
        <label>
          База 1С
          <select value={base} onChange={(event) => setBase(event.target.value)}>
            <option value="AQMOL">AQMOL</option>
            <option value="NSK">NSK</option>
            <option value="IP">IP</option>
          </select>
        </label>
        <label>
          Файл xlsx
          <input type="file" accept=".xlsx" onChange={(event) => setFile(event.target.files?.[0] || null)} />
        </label>
        <label>
          <span>
            <input type="checkbox" checked={runSync} onChange={(event) => setRunSync(event.target.checked)} />{" "}
            Сразу запустить синхронизацию
          </span>
        </label>
        <div className="fin-actions">
          <button
            type="button"
            className="fin-btn fin-btn--primary"
            disabled={!file}
            onClick={async () => {
              onError(null);
              try {
                const result = await importOneCReport({ base, file, runSync });
                onInfo(
                  `Принято: ${result.file_name}, строк ${result.rows ?? 0}, ошибок ${result.errors?.length || 0}`
                );
                await refresh();
              } catch (error) {
                onError(error.message);
              }
            }}
          >
            Загрузить 1С
          </button>
          <button
            type="button"
            className="fin-btn"
            onClick={async () => {
              onError(null);
              try {
                const result = await triggerJournalSync({ wait: true });
                onInfo(result.message || "Синхронизация запущена");
                await refresh();
              } catch (error) {
                onError(error.message);
              }
            }}
          >
            Синхронизировать журнал
          </button>
        </div>
        {status && (
          <div className="fin-muted">
            Последний прогон: {status.status}, с {status.started_at}
            {status.error_text ? `, ошибка: ${status.error_text}` : ""}
          </div>
        )}
        {runs.map((run) => (
          <div key={run.id} className="fin-muted">
            {run.started_at} · {run.source} · {run.status} · прочитано {run.rows_read ?? 0}, записано{" "}
            {run.rows_upserted ?? 0}
          </div>
        ))}
      </div>
    </div>
  );
}

function PeriodsTab({ onError, onInfo }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [periods, setPeriods] = useState([]);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    const items = await getPeriods({ year });
    setPeriods(Array.isArray(items) ? items : []);
  }, [year]);

  useEffect(() => {
    load().catch((error) => onError(error.message));
  }, [load, onError]);

  const change = async (period, action) => {
    onError(null);
    try {
      if (action === "close") await closePeriod(period.year, period.month, reason);
      else await openPeriod(period.year, period.month, reason);
      onInfo(action === "close" ? "Период закрыт" : "Период открыт");
      await load();
    } catch (error) {
      onError(error.message);
    }
  };

  return (
    <div className="fin-panel">
      <div className="fin-grid">
        <label>
          Год
          <input type="number" value={year} onChange={(event) => setYear(Number(event.target.value))} />
        </label>
        <label>
          Основание
          <input value={reason} onChange={(event) => setReason(event.target.value)} />
        </label>
      </div>
      <table className="fin-table">
        <thead>
          <tr>
            <th>Месяц</th>
            <th>Статус</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {periods.map((period) => (
            <tr key={`${period.year}-${period.month}`}>
              <td>{period.month}.{period.year}</td>
              <td>{period.status}</td>
              <td>
                {period.status === "closed" ? (
                  <button type="button" className="fin-btn" onClick={() => change(period, "open")}>Открыть</button>
                ) : (
                  <button type="button" className="fin-btn" onClick={() => change(period, "close")}>Закрыть</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UnmappedTab({ onError }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    getUnmappedValues({ limit: 100 })
      .then((items) => setRows(Array.isArray(items) ? items : []))
      .catch((error) => onError(error.message));
  }, [onError]);

  return (
    <div className="fin-panel">
      <table className="fin-table">
        <thead>
          <tr>
            <th>Поле</th>
            <th>Значение</th>
            <th>Строк</th>
            <th>Сумма</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.field}-${row.source_value}`}>
              <td>{row.field}</td>
              <td>{row.source_value}</td>
              <td className="is-num">{row.rows}</td>
              <td className="is-num">{row.amount_kzt}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <div className="fin-muted">Нераспознанных значений нет</div>}
    </div>
  );
}

export default Administration;
